import { AnimationEvent, animateChild, query, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { PrimeNGConfig } from 'primeng/api';
import { UniqueComponentId } from 'primeng/utils';

import { Message } from '@core/interfaces';
import { NotificationService } from '@core/services';

@Component({
  selector: 'z-toast',
  templateUrl: './toast.component.html',
  animations: [trigger('toastAnimation', [transition(':enter, :leave', [query('@*', animateChild())])])],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastComponent implements OnInit, AfterViewInit, OnDestroy {
  destroy$ = new Subject<boolean>();

  @Input() key: string;
  @Input() baseZIndex: number = 4000;
  @Input() position: string = 'top-right';

  @Input() style: any;
  @Input() styleClass: string;
  @Input() breakpoints: any;

  @Input() preventOpenDuplicates: boolean = true;
  @Input() preventDuplicates: boolean = false;

  @Input() showTransformOptions: string = 'translateY(100%)';
  @Input() hideTransformOptions: string = 'translateY(-100%)';
  @Input() showTransitionOptions: string = '300ms ease-out';
  @Input() hideTransitionOptions: string = '250ms ease-in';

  @Output() close: EventEmitter<any> = new EventEmitter();

  @ViewChild('container') containerViewChild: ElementRef;

  messages: Message[];
  archived: Message[];

  constructor(public notificationService: NotificationService, private cd: ChangeDetectorRef, public config: PrimeNGConfig) {}

  styleElement: any;

  id: string = UniqueComponentId();

  ngOnInit() {
    this.notificationService.messageSource$
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe((messages) => {
        if (messages instanceof Array) {
          const filteredMessages = messages.filter((m) => this.canAdd(m));
          this.add(filteredMessages);
        } else if (this.canAdd(messages)) {
          this.add([messages]);
        }
      });

    this.notificationService.clearSource$.pipe(takeUntil(this.destroy$)).subscribe((key) => {
      if (key) {
        if (this.key === key) {
          this.messages = null;
        }
      } else {
        this.messages = null;
      }

      this.cd.markForCheck();
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    if (this.styleElement) {
      document.head.removeChild(this.styleElement);
      this.styleElement = null;
    }
  }

  ngAfterViewInit() {
    if (this.breakpoints) {
      this.createStyle();
    }
  }

  add(messages: Message[]): void {
    this.messages = this.messages ? [...this.messages, ...messages] : [...messages];

    if (this.preventDuplicates) {
      this.archived = this.archived ? [...this.archived, ...messages] : [...messages];
    }

    this.cd.markForCheck();
  }

  canAdd(message: Message): boolean {
    let allow = this.key === message.key;

    if (allow && this.preventOpenDuplicates) {
      allow = !this.containsMessage(this.messages, message);
    }

    if (allow && this.preventDuplicates) {
      allow = !this.containsMessage(this.archived, message);
    }

    return allow;
  }

  containsMessage(collection: Message[], message: Message): boolean {
    if (!collection) {
      return false;
    }

    return (
      collection.find((m) => {
        return m.title === message.title && m.message === message.message && m.severity === message.severity;
      }) != null
    );
  }

  onMessageOpen(event: any) {
    this.notificationService.messageOpen$.next(event.message);
  }

  onMessageClose(event: any) {
    this.messages.splice(event.index, 1);

    this.close.emit({
      message: event.message,
    });

    this.notificationService.messageClose$.next(event.message);

    this.cd.detectChanges();
  }

  onAnimationStart(event: AnimationEvent) {
    if (event.fromState === 'void') {
      this.containerViewChild.nativeElement.setAttribute(this.id, '');
      this.containerViewChild.nativeElement.style.zIndex = this.baseZIndex;
    }
  }

  onAnimationEnd(event: AnimationEvent) {
    if (event.toState === 'void') {
      return;
    }
  }

  createStyle() {
    if (!this.styleElement) {
      this.styleElement = document.createElement('style');
      this.styleElement.type = 'text/css';

      document.head.appendChild(this.styleElement);

      let innerHTML = '';

      for (const breakpoint in this.breakpoints) {
        let breakpointStyle = '';

        for (const styleProp in this.breakpoints[breakpoint]) {
          const property: string = this.breakpoints[breakpoint][styleProp];
          breakpointStyle += `${styleProp}:${property} !important;`;
        }

        innerHTML += `
                    @media screen and (max-width: ${breakpoint}) {
                        .p-toast[${this.id}] {
                           ${breakpointStyle}
                        }
                    }
                `;
      }

      this.styleElement.innerHTML = innerHTML;
    }
  }
}
